/**
 * LuminisClient — one transport abstraction for the mobile PWA (task-6).
 *
 * `mode: 'mock'` keeps the demo path (MockDaemon) exactly as before.
 * `mode: 'daemon'` implements the real HTTP API of the PC daemon
 * (`apps/server`): bearer device token, JSON errors mapped to protocol error
 * codes, request timeouts, `POST /chat` SSE over `fetch` + `ReadableStream`
 * with AbortController stop-generation, read-only `/sessions` + tail, and
 * `/approvals` + signed `/approval` decisions.
 *
 * E2E: control/approval traffic is wrapped in envelopes (docs/PROTOCOL-v0.md).
 * The client exposes `sealEnvelope`/`openEnvelope` over the channel key derived
 * at pairing time (X25519 + HKDF + AES-256-GCM); the direct-HTTP daemon path is
 * loopback/dev only today and is reached through the E2E relay in a later task.
 *
 * No network access happens at import time; everything is injectable
 * (`fetchImpl`, identity, channel key) so tests run offline with fetch doubles.
 */
import type {
  ApprovalDecision,
  ApprovalRequest,
  ChatDelta,
  ChatDone,
  ChatError,
  Envelope,
  ErrorCode,
  HealthPayload,
  MessageType,
  ModelSelection,
  ModelsResponse,
  RiskLevel,
  SessionEvent,
  SessionSource,
  SessionStatus,
  SessionSummary,
  Usage,
} from '@luminis/protocol';
import { PROTOCOL_VERSION, isSessionControlInfo } from '@luminis/protocol';
import {
  alarmCreateBody,
  alarmFromUnknown,
  alarmPatchBody,
  alarmsFromUnknown,
  nextAlarmAt,
  normalizeAlarmDraft,
  normalizeAlarmPatch,
  sortAlarms,
  upsertAlarm,
  demoAlarms,
  type AlarmDraft,
  type AlarmPatch,
} from '../alarm';
import { validateDecision } from '../approval';
import { MODEL_OPTIONS } from '../settings';
import {
  demoTodos,
  normalizeTodoDraft,
  normalizeTodoPatch,
  sortTodos,
  todoCreateBody,
  todoFromUnknown,
  todoPatchBody,
  todosFromUnknown,
  upsertTodo,
  type TodoDraft,
  type TodoPatch,
} from '../todo';
import { decryptEnvelope, encryptEnvelope, newApprovalNonce, signApproval } from '../crypto/e2e';
import { nowSeconds, randomId } from '../id';
import { demoMemories, memoryEntriesFromUnknown, type MemoryEntry } from '../memory';
import {
  qqApplyPermissionsPatch,
  qqPermissionsBody,
  qqStatusFromUnknown,
  type QqPermissionsPatch,
  type QqStatus,
} from '../qq';
import { sessionControlFromSummary } from '../session-control';
import { SSEDecoder } from '../sse';
import type {
  Alarm,
  ApprovalView,
  ChatTurn,
  DeviceIdentityStored,
  SessionEventKind,
  SessionEventView,
  SessionView,
  Todo,
} from '../types';
import { MockDaemon, type MockStreamRequest } from './mock';
import type { ChatStreamHandle, ChatStreamHandlers } from './worker';

export type LuminisMode = 'mock' | 'daemon';

/** Failures raised by the client layer; `code` is a protocol error code. */
export class LuminisClientError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly retryable: boolean;

  constructor(
    code: ErrorCode,
    message: string,
    options: { status?: number; retryable?: boolean } = {},
  ) {
    super(message);
    this.name = 'LuminisClientError';
    this.code = code;
    this.status = options.status ?? 0;
    this.retryable = options.retryable ?? false;
  }
}

export const DEFAULT_MODEL: ModelSelection = {
  provider: 'deepseek',
  model: 'deepseek-flash',
};

const DEFAULT_TIMEOUT_MS = 15_000;
const DEFAULT_SESSION_DAYS = 7;

// ---------------------------------------------------------------------------
// Shared request/response shapes
// ---------------------------------------------------------------------------

export interface LuminisChatRequest {
  text: string;
  conversation_id?: string;
  persona_id?: string;
  /** Model selection (provider/model + optional thinking/effort). */
  model?: ModelSelection;
  /** Prior turns (mock daemon consumes them; the daemon endpoint keeps its own history). */
  history?: ChatTurn[];
  /** Per-turn agent tool-round budget (1..50). */
  max_tool_rounds?: number;
  source?: 'user' | 'external';
}

export interface DaemonToolCallEvent {
  call_id: string;
  name: string;
  args: Record<string, unknown>;
  risk?: RiskLevel;
}

export interface DaemonToolResultEvent {
  call_id: string;
  name: string;
  ok: boolean;
  error?: { code: ErrorCode; message: string };
  duration_ms?: number;
}

/** Chat handlers plus the daemon agent events the UI may surface later. */
export interface DaemonChatHandlers extends ChatStreamHandlers {
  onToolCall?(call: DaemonToolCallEvent): void;
  onToolResult?(result: DaemonToolResultEvent): void;
  onApprovalRequired?(approval: ApprovalRequest): void;
  onUsage?(usage: Usage): void;
}

export interface ClientSessionQuery {
  days?: number;
  activeOnly?: boolean;
  source?: SessionSource;
  limit?: number;
}

export interface ClientTailQuery {
  limit?: number;
  cursor?: string;
}

export interface ClientTailResult {
  events: SessionEventView[];
  next_cursor?: string;
}

export interface DecideApprovalOutcome {
  /** `true` when the decision was recorded (grant *or* deny). */
  ok: boolean;
  error?: string;
  code?: ErrorCode;
  /** Updated view when the backend returned one. */
  approval?: ApprovalView;
}

export interface DecideApprovalOptions {
  confirmHighRisk: boolean;
  /** Override the client-side signature nonce (tests only). */
  nonce?: string;
}

/**
 * `POST /alarms/:id/fired` outcome. The daemon normally returns the advanced
 * alarm row; a bare `{ ok: true }` is accepted as an ack with `alarm: null`, in
 * which case the caller must refresh the list rather than invent `next_at`.
 */
export interface AlarmFiredResult {
  ok: true;
  alarm: Alarm | null;
}

export interface E2EChannel {
  /** AES-256-GCM key derived from the X25519 handshake at pairing time. */
  key: CryptoKey;
  /** AAD device binding (defaults to the identity's device id). */
  deviceId?: string;
}

export interface LuminisClient {
  readonly mode: LuminisMode;
  health(): Promise<HealthPayload>;
  models(): Promise<ModelsResponse>;
  approvals(): Promise<ApprovalView[]>;
  decideApproval(
    approval: ApprovalView,
    decision: ApprovalDecision,
    options: DecideApprovalOptions,
  ): Promise<DecideApprovalOutcome>;
  sessions(query?: ClientSessionQuery): Promise<SessionView[]>;
  sessionTail(
    source: SessionSource,
    sessionId: string,
    query?: ClientTailQuery,
  ): Promise<ClientTailResult>;
  /**
   * QQ bot management (v0.5.0): daemon `/qq/*`, device-auth like the other
   * daemon APIs. Values are re-validated by `qqStatusFromUnknown`.
   */
  qqStatus(): Promise<QqStatus>;
  qqSetPermissions(patch: QqPermissionsPatch): Promise<QqStatus>;
  qqDisconnect(): Promise<{ ok: boolean }>;
  /**
   * Long-term memory (v0.5.0): daemon `/memories`, device-auth like the other
   * daemon APIs. Values are re-validated by `memoryEntriesFromUnknown`.
   */
  memories(): Promise<MemoryEntry[]>;
  deleteMemory(id: string): Promise<MemoryEntry[]>;
  clearMemories(): Promise<{ ok: boolean }>;
  approveMemory(id: string): Promise<MemoryEntry[]>;
  /**
   * Todo + alarm lists (v0.6.0): daemon `/todos` and `/alarms`, device-auth like
   * the other daemon APIs. Responses are re-validated by `todosFromUnknown` /
   * `alarmsFromUnknown`; a malformed payload throws instead of returning rows.
   */
  todos(): Promise<Todo[]>;
  createTodo(input: TodoDraft): Promise<Todo>;
  updateTodo(id: string, patch: TodoPatch): Promise<Todo>;
  /** Returns the daemon's remaining list when included, else null. */
  deleteTodo(id: string): Promise<Todo[] | null>;
  toggleTodo(id: string): Promise<Todo>;
  alarms(): Promise<Alarm[]>;
  createAlarm(input: AlarmDraft): Promise<Alarm>;
  updateAlarm(id: string, patch: AlarmPatch): Promise<Alarm>;
  /** Returns the daemon's remaining list when included, else null. */
  deleteAlarm(id: string): Promise<Alarm[] | null>;
  /** Acknowledge that a native/in-page alarm fired so the daemon can advance it. */
  alarmFired(id: string): Promise<AlarmFiredResult>;
  streamChat(
    request: LuminisChatRequest,
    handlers: DaemonChatHandlers,
    options?: { messageId?: string },
  ): ChatStreamHandle;
  /** Wrap a control/approval payload in an E2E envelope (requires channel key). */
  sealEnvelope(
    type: MessageType,
    payload: unknown,
    options?: { id?: string; ts?: number },
  ): Promise<Envelope>;
  /** Decrypt and parse an E2E envelope (requires channel key). */
  openEnvelope<T = unknown>(envelope: Envelope): Promise<T>;
  /** Abort any in-flight chat stream and release timers. */
  dispose(): void;
}

export interface MockClientOptions {
  mode: 'mock';
  daemon: MockDaemon;
  identity: DeviceIdentityStored;
  channel?: E2EChannel;
}

export interface DaemonClientOptions {
  mode: 'daemon';
  /** e.g. `http://127.0.0.1:8787` (loopback) — https when exposed via a tunnel. */
  baseUrl: string;
  /** Device token issued by pairing (sent as `Authorization: Bearer`). */
  token: string;
  /** Device identity used to sign approval decisions. */
  identity: DeviceIdentityStored;
  /** Injected for tests; defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /** JSON request timeout (chat only times the response headers). */
  timeoutMs?: number;
  /** AAD device binding override; defaults to `identity.device_id`. */
  deviceId?: string;
  /** Derived E2E channel key for `sealEnvelope`/`openEnvelope`. */
  channel?: E2EChannel;
  /** Clock injection for tests (seconds). */
  clock?: () => number;
}

export type CreateLuminisClientOptions = MockClientOptions | DaemonClientOptions;

export function createLuminisClient(options: CreateLuminisClientOptions): LuminisClient {
  return options.mode === 'mock'
    ? new MockLuminisClient(options)
    : new DaemonLuminisClient(options);
}

// ---------------------------------------------------------------------------
// Guard helpers
// ---------------------------------------------------------------------------

const RISK_LEVELS: ReadonlySet<string> = new Set(['read', 'write', 'external', 'destructive']);
const SESSION_SOURCES: ReadonlySet<string> = new Set([
  'dsh',
  'codex',
  'opencode',
  'vscode',
  'luminis',
]);
const SESSION_STATUSES: ReadonlySet<string> = new Set(['active', 'idle', 'done', 'error']);
const SESSION_EVENT_KINDS: ReadonlySet<string> = new Set([
  'message',
  'tool',
  'status',
  'file',
  'progress',
]);
const APPROVAL_SOURCES: ReadonlySet<string> = new Set(['user', 'external', 'system']);

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<ErrorCode>([
  'AUTH_REQUIRED',
  'AUTH_INVALID',
  'APPROVAL_REQUIRED',
  'APPROVAL_EXPIRED',
  'APPROVAL_DENIED',
  'RISK_BLOCKED',
  'TOOL_NOT_FOUND',
  'EXEC_TIMEOUT',
  'PLUGIN_INVALID',
  'RATE_LIMIT',
  'INVALID_REQUEST',
  'NOT_FOUND',
  'CONFLICT',
  'INTERNAL',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function toSeconds(value: number | undefined): number | undefined {
  if (value === undefined) return undefined;
  // Daemon rows use epoch milliseconds; protocol wire values use seconds.
  return Math.floor(value > 1e11 ? value / 1000 : value);
}

function knownErrorCode(value: unknown): ErrorCode | undefined {
  return typeof value === 'string' && KNOWN_ERROR_CODES.has(value)
    ? (value as ErrorCode)
    : undefined;
}

function fallbackCodeForStatus(status: number): ErrorCode {
  if (status === 400) return 'INVALID_REQUEST';
  if (status === 401) return 'AUTH_INVALID';
  if (status === 403) return 'RISK_BLOCKED';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 410) return 'APPROVAL_EXPIRED';
  if (status === 422) return 'PLUGIN_INVALID';
  if (status === 429) return 'RATE_LIMIT';
  if (status === 504) return 'EXEC_TIMEOUT';
  return status >= 500 ? 'INTERNAL' : 'INVALID_REQUEST';
}

interface ParsedError {
  code: ErrorCode;
  message: string;
  retryable: boolean;
}

/** Accept both `{ok:false,error:{code,message}}` and `{error:{...}}` bodies. */
function parseErrorBody(body: unknown, status: number): ParsedError {
  const record = isRecord(body) ? body : {};
  const errorRecord = isRecord(record.error) ? record.error : record;
  const explicitCode = knownErrorCode(errorRecord.code);
  const message = asString(errorRecord.message) ?? `daemon 请求失败（HTTP ${status}）`;
  return {
    code: explicitCode ?? fallbackCodeForStatus(status),
    message,
    retryable: errorRecord.retryable === true || status >= 500 || status === 429 || status === 0,
  };
}

export function approvalViewFromRequest(
  request: ApprovalRequest,
  requestedAt: number,
): ApprovalView {
  return {
    approval_id: request.approval_id,
    tool: request.tool,
    args_hash: request.args_hash,
    risk: request.risk,
    summary: request.summary,
    source: request.source,
    expires_at: request.expires_at,
    requested_at: requestedAt,
    status: 'pending',
    ...(request.task_id ? { task_id: request.task_id } : {}),
    ...(request.plan ? { plan: request.plan } : {}),
  };
}

/** Map an `ApprovalRequest` (daemon wire shape, no token) from untrusted JSON. */
export function approvalRequestFromUnknown(value: unknown): ApprovalRequest | null {
  if (!isRecord(value)) return null;
  const risk = asString(value.risk);
  const source = asString(value.source);
  const expiresAt = asFiniteNumber(value.expires_at);
  if (
    typeof value.approval_id !== 'string' ||
    typeof value.tool !== 'string' ||
    typeof value.args_hash !== 'string' ||
    typeof value.summary !== 'string' ||
    expiresAt === undefined ||
    !risk ||
    !RISK_LEVELS.has(risk) ||
    !source ||
    !APPROVAL_SOURCES.has(source)
  ) {
    return null;
  }
  const plan = Array.isArray(value.plan)
    ? value.plan.filter((step): step is string => typeof step === 'string')
    : undefined;
  return {
    approval_id: value.approval_id,
    tool: value.tool,
    args_hash: value.args_hash,
    risk: risk as RiskLevel,
    summary: value.summary,
    source: source as ApprovalRequest['source'],
    expires_at: expiresAt,
    ...(typeof value.task_id === 'string' ? { task_id: value.task_id } : {}),
    ...(plan && plan.length > 0 ? { plan } : {}),
  };
}

const APPROVAL_VIEW_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'granted',
  'denied',
  'expired',
  'revoked',
]);

/** Map the daemon's stored approval row (id/ms timestamps) into the PWA view. */
function approvalViewFromRow(value: unknown): ApprovalView | undefined {
  if (!isRecord(value)) return undefined;
  const id = asString(value.id) ?? asString(value.approval_id);
  const status = asString(value.status);
  const risk = asString(value.risk);
  const source = asString(value.source);
  if (
    !id ||
    typeof value.tool !== 'string' ||
    typeof value.args_hash !== 'string' ||
    typeof value.summary !== 'string' ||
    !status ||
    !APPROVAL_VIEW_STATUSES.has(status) ||
    !risk ||
    !RISK_LEVELS.has(risk) ||
    !source ||
    !APPROVAL_SOURCES.has(source)
  ) {
    return undefined;
  }
  if (status === 'cancelled') return undefined;
  const requestedAt = toSeconds(asFiniteNumber(value.requested_at)) ?? 0;
  const expiresAt = toSeconds(asFiniteNumber(value.expires_at)) ?? 0;
  const decidedAt = toSeconds(asFiniteNumber(value.decided_at));
  let plan: string[] | undefined;
  if (Array.isArray(value.plan)) {
    plan = value.plan.filter((step): step is string => typeof step === 'string');
  } else if (typeof value.plan_json === 'string') {
    try {
      const parsed: unknown = JSON.parse(value.plan_json);
      if (Array.isArray(parsed)) {
        plan = parsed.filter((step): step is string => typeof step === 'string');
      }
    } catch {
      plan = undefined;
    }
  }
  return {
    approval_id: id,
    tool: value.tool,
    args_hash: value.args_hash,
    risk: risk as RiskLevel,
    summary: value.summary,
    source: source as ApprovalView['source'],
    expires_at: expiresAt,
    requested_at: requestedAt,
    status: status as ApprovalView['status'],
    ...(typeof value.task_id === 'string' ? { task_id: value.task_id } : {}),
    ...(typeof value.decision === 'string'
      ? { decision: value.decision as ApprovalView['decision'] }
      : {}),
    ...(typeof value.scope === 'string' ? { scope: value.scope as ApprovalView['scope'] } : {}),
    ...(decidedAt !== undefined ? { decided_at: decidedAt } : {}),
    ...(typeof value.signature === 'string' && value.signature.length > 0
      ? { signature: value.signature }
      : {}),
    ...(plan && plan.length > 0 ? { plan } : {}),
  };
}

/**
 * Extract the mutated row from a `/todos` response. Accepts `{ todo }`, a bare
 * row, or a full `{ todos }`/array response (matched by id). Returns null when
 * the response carries no authoritative row, so the caller can fail loudly.
 */
function todoMutationFromResponse(response: unknown, id?: string): Todo | null {
  if (isRecord(response)) {
    const direct = todoFromUnknown(response.todo);
    if (direct !== null) return direct;
    const list = todosFromUnknown(response);
    if (list !== null) {
      if (id !== undefined) return list.find((todo) => todo.id === id) ?? null;
      return list.length === 1 ? (list[0] ?? null) : null;
    }
  }
  return todoFromUnknown(response);
}

function alarmMutationFromResponse(response: unknown, id?: string): Alarm | null {
  if (isRecord(response)) {
    const direct = alarmFromUnknown(response.alarm);
    if (direct !== null) return direct;
    const list = alarmsFromUnknown(response);
    if (list !== null) {
      if (id !== undefined) return list.find((alarm) => alarm.id === id) ?? null;
      return list.length === 1 ? (list[0] ?? null) : null;
    }
  }
  return alarmFromUnknown(response);
}

/**
 * DELETE handlers only succeed when the daemon explicitly acknowledges the
 * deletion (`204`, `{ ok: true }`, or the removed row). An empty 200 body is
 * already rejected by `requestJson`, and `{ ok: false }` must never look like
 * success.
 */
function assertDeleteAcknowledged(response: unknown, label: string): void {
  if (response === null) return;
  if (isRecord(response) && response.ok === true) return;
  if (
    isRecord(response) &&
    (typeof response.id === 'string' || isRecord(response.todo) || isRecord(response.alarm))
  ) {
    return;
  }
  throw new LuminisClientError('INTERNAL', `daemon ${label} 未确认删除`);
}

export const MAX_SESSION_TITLE_CHARS = 200;
export const MAX_SESSION_EVENT_TEXT_CHARS = 4000;
export const MAX_SESSION_EVENT_TOOL_CHARS = 128;
export const MAX_SESSION_EVENT_ROLE_CHARS = 64;

function cappedString(value: string, maxChars: number): string {
  return value.length > maxChars ? `${value.slice(0, maxChars)}…` : value;
}

export function sessionViewFromSummary(
  summary: SessionSummary,
  fallbackTimestamp: number,
): SessionView | null {
  if (!SESSION_SOURCES.has(summary.source)) return null;
  if (!SESSION_STATUSES.has(summary.status)) return null;
  if (typeof summary.session_id !== 'string' || summary.session_id.length === 0) return null;
  const meta = isRecord(summary.meta) ? summary.meta : {};
  const progress = asFiniteNumber(meta.progress);
  const startedAt = toSeconds(asFiniteNumber(meta.started_at));
  const phase = asString(meta.phase);
  const model = asString(meta.model);
  const parentSessionId = asString(meta.parent_session_id)?.trim();
  const control = isSessionControlInfo(summary.control)
    ? sessionControlFromSummary(summary.control)
    : undefined;
  const lastEventAt = asFiniteNumber(summary.last_event_at) ?? fallbackTimestamp;
  const rawTitle =
    typeof summary.title === 'string' && summary.title.trim().length > 0
      ? summary.title.trim()
      : '';
  return {
    source: summary.source,
    session_id: summary.session_id,
    // Untrusted connector titles are capped defensively (UI also truncates).
    title: cappedString(rawTitle || summary.session_id, MAX_SESSION_TITLE_CHARS),
    status: summary.status as SessionStatus,
    last_event_at: lastEventAt,
    tokens: asFiniteNumber(summary.tokens),
    cost_cny: asFiniteNumber(summary.cost_cny),
    events: [],
    ...(startedAt !== undefined ? { started_at: startedAt } : {}),
    ...(progress !== undefined
      ? { progress: Math.min(100, Math.max(0, Math.round(progress))) }
      : {}),
    ...(phase ? { phase: cappedString(phase, 64) } : {}),
    ...(model ? { model: cappedString(model, 64) } : {}),
    ...(parentSessionId ? { parent_session_id: cappedString(parentSessionId, 200) } : {}),
    ...(control ? { control } : {}),
  };
}

export function sessionEventFromWire(value: unknown): SessionEventView | null {
  if (!isRecord(value)) return null;
  const kind = asString(value.kind);
  const ts = asFiniteNumber(value.ts);
  if (!kind || !SESSION_EVENT_KINDS.has(kind) || ts === undefined) return null;
  const role = asString(value.role);
  const text = asString(value.text);
  const tool = asString(value.tool);
  return {
    ts: Math.floor(ts),
    kind: kind as SessionEventKind,
    ...(role !== undefined ? { role: cappedString(role, MAX_SESSION_EVENT_ROLE_CHARS) } : {}),
    ...(text !== undefined ? { text: cappedString(text, MAX_SESSION_EVENT_TEXT_CHARS) } : {}),
    ...(tool !== undefined ? { tool: cappedString(tool, MAX_SESSION_EVENT_TOOL_CHARS) } : {}),
  };
}

function chatErrorEvent(messageId: string, error: unknown): ChatError {
  if (error instanceof LuminisClientError) {
    return {
      message_id: messageId,
      code: error.code,
      message: error.message,
      retryable: error.retryable,
    };
  }
  return {
    message_id: messageId,
    code: 'INTERNAL',
    message: error instanceof Error ? error.message : 'daemon 请求失败',
    retryable: false,
  };
}

// ---------------------------------------------------------------------------
// `POST /chat` SSE parsing (daemon agent events)
// ---------------------------------------------------------------------------

export type DaemonChatStreamEvent =
  | { type: 'delta'; delta: ChatDelta }
  | { type: 'done'; done: ChatDone }
  | { type: 'error'; error: ChatError }
  | { type: 'usage'; usage: Usage }
  | { type: 'tool_call'; call: DaemonToolCallEvent }
  | { type: 'tool_result'; result: DaemonToolResultEvent }
  | { type: 'approval_required'; approval: ApprovalRequest };

/**
 * Parses the daemon's `POST /chat` SSE stream:
 * `delta | tool_call | tool_result | approval_required | usage | done | error`,
 * tolerating arbitrary chunk boundaries. A stream that ends without a terminal
 * `done`/`error` frame is reported as a retryable error (no silent truncation).
 */
export class DaemonChatStreamParser {
  private readonly decoder = new SSEDecoder();
  private readonly messageId: string;
  private readonly model: ModelSelection;
  private usage: Usage | undefined;
  private done = false;

  constructor(options: { messageId: string; model?: ModelSelection }) {
    this.messageId = options.messageId;
    this.model = options.model ?? DEFAULT_MODEL;
  }

  get isDone(): boolean {
    return this.done;
  }

  push(chunk: string): DaemonChatStreamEvent[] {
    return this.decoder.push(chunk).flatMap((event) => this.handle(event.event, event.data));
  }

  finish(): DaemonChatStreamEvent[] {
    const events = this.decoder.finish().flatMap((event) => this.handle(event.event, event.data));
    if (!this.done) {
      this.done = true;
      events.push({
        type: 'error',
        error: {
          message_id: this.messageId,
          code: 'INTERNAL',
          message: 'daemon 流式响应意外结束（未收到 done）',
          retryable: true,
        },
      });
    }
    return events;
  }

  private handle(eventName: string, data: string): DaemonChatStreamEvent[] {
    if (this.done) return [];
    if (eventName === '') return [];
    let payload: Record<string, unknown>;
    try {
      const parsed: unknown = JSON.parse(data);
      if (!isRecord(parsed)) return [];
      payload = parsed;
    } catch {
      this.done = true;
      return [
        {
          type: 'error',
          error: {
            message_id: this.messageId,
            code: 'INTERNAL',
            message: '无法解析 daemon 流式响应',
            retryable: false,
          },
        },
      ];
    }
    switch (eventName) {
      case 'delta':
        return this.handleDelta(payload);
      case 'usage':
        return this.handleUsage(payload);
      case 'done':
        return this.handleDone(payload);
      case 'error':
        return this.handleError(payload);
      case 'tool_call':
        return this.handleToolCall(payload);
      case 'tool_result':
        return this.handleToolResult(payload);
      case 'approval_required':
        return this.handleApprovalRequired(payload);
      default:
        return [];
    }
  }

  private handleDelta(payload: Record<string, unknown>): DaemonChatStreamEvent[] {
    const kind = payload.kind;
    const delta = asString(payload.delta);
    if ((kind !== 'content' && kind !== 'reasoning') || delta === undefined) return [];
    return [
      {
        type: 'delta',
        delta: {
          message_id: asString(payload.message_id) ?? this.messageId,
          seq: asFiniteNumber(payload.seq) ?? 0,
          kind,
          delta,
        },
      },
    ];
  }

  private handleUsage(payload: Record<string, unknown>): DaemonChatStreamEvent[] {
    const raw = isRecord(payload.usage) ? payload.usage : undefined;
    if (!raw) return [];
    const input = asFiniteNumber(raw.input_tokens);
    const output = asFiniteNumber(raw.output_tokens);
    if (input === undefined || output === undefined) return [];
    const usage: Usage = { input_tokens: input, output_tokens: output };
    const cached = asFiniteNumber(raw.cached_input_tokens);
    const cost = asFiniteNumber(raw.cost_cny);
    if (cached !== undefined) usage.cached_input_tokens = cached;
    if (cost !== undefined) usage.cost_cny = cost;
    this.usage = usage;
    return [{ type: 'usage', usage }];
  }

  private handleDone(payload: Record<string, unknown>): DaemonChatStreamEvent[] {
    this.done = true;
    const events: DaemonChatStreamEvent[] = [];
    const approval = approvalRequestFromUnknown(payload.approval);
    if (approval && payload.status === 'approval_required') {
      events.push({ type: 'approval_required', approval });
    }
    const rawUsage = isRecord(payload.usage)
      ? this.handleUsage({ usage: payload.usage })[0]
      : undefined;
    const usage = rawUsage?.type === 'usage' ? rawUsage.usage : this.usage;
    const rawModel = isRecord(payload.model) ? payload.model : undefined;
    const model: ModelSelection =
      rawModel && typeof rawModel.provider === 'string' && typeof rawModel.model === 'string'
        ? {
            provider: rawModel.provider,
            model: rawModel.model,
            ...(rawModel.thinking === 'enabled' || rawModel.thinking === 'disabled'
              ? { thinking: rawModel.thinking }
              : {}),
            ...(rawModel.effort === 'low' || rawModel.effort === 'high' || rawModel.effort === 'max'
              ? { effort: rawModel.effort }
              : {}),
          }
        : this.model;
    events.push({
      type: 'done',
      done: {
        message_id: asString(payload.message_id) ?? this.messageId,
        model,
        ...(usage ? { usage } : {}),
        ...(asString(payload.finish_reason)
          ? { finish_reason: asString(payload.finish_reason) }
          : {}),
      },
    });
    return events;
  }

  private handleError(payload: Record<string, unknown>): DaemonChatStreamEvent[] {
    this.done = true;
    return [
      {
        type: 'error',
        error: {
          message_id: asString(payload.message_id) ?? this.messageId,
          code: knownErrorCode(payload.code) ?? 'INTERNAL',
          message: asString(payload.message) ?? 'daemon 返回了错误',
          retryable: payload.retryable === true,
        },
      },
    ];
  }

  private handleToolCall(payload: Record<string, unknown>): DaemonChatStreamEvent[] {
    const callId = asString(payload.call_id);
    const name = asString(payload.name);
    if (!callId || !name) return [];
    const risk = asString(payload.risk);
    return [
      {
        type: 'tool_call',
        call: {
          call_id: callId,
          name,
          args: isRecord(payload.args) ? payload.args : {},
          ...(risk && RISK_LEVELS.has(risk) ? { risk: risk as RiskLevel } : {}),
        },
      },
    ];
  }

  private handleToolResult(payload: Record<string, unknown>): DaemonChatStreamEvent[] {
    const callId = asString(payload.call_id);
    const name = asString(payload.name);
    if (!callId || !name) return [];
    const errorRecord = isRecord(payload.error) ? payload.error : undefined;
    const code = knownErrorCode(errorRecord?.code);
    const message = asString(errorRecord?.message);
    return [
      {
        type: 'tool_result',
        result: {
          call_id: callId,
          name,
          ok: payload.ok === true,
          ...(code && message ? { error: { code, message } } : {}),
          ...(asFiniteNumber(payload.duration_ms) !== undefined
            ? { duration_ms: asFiniteNumber(payload.duration_ms) }
            : {}),
        },
      },
    ];
  }

  private handleApprovalRequired(payload: Record<string, unknown>): DaemonChatStreamEvent[] {
    const approval = approvalRequestFromUnknown(payload.approval);
    return approval ? [{ type: 'approval_required', approval }] : [];
  }
}

// ---------------------------------------------------------------------------
// Mock client (demo mode: behaviour identical to MockDaemon)
// ---------------------------------------------------------------------------

export class MockLuminisClient implements LuminisClient {
  readonly mode = 'mock' as const;
  private readonly daemon: MockDaemon;
  private readonly identity: DeviceIdentityStored;
  private readonly channel: E2EChannel | undefined;
  private seededApprovals: ApprovalView[] | null = null;
  private readonly streams = new Set<ChatStreamHandle>();
  /** Local demo-only QQ permission snapshot (never a real connection). */
  private qq: QqStatus = { enabled: false, connected: false, chats: [] };
  /** Local demo-only memory seed (never persisted; clearly mock in the UI). */
  private seededMemories: MemoryEntry[] | null = null;
  /** Local demo-only todo/alarm rows (never persisted by the client). */
  private seededTodos: Todo[] | null = null;
  private seededAlarms: Alarm[] | null = null;

  constructor(options: Omit<MockClientOptions, 'mode'>) {
    this.daemon = options.daemon;
    this.identity = options.identity;
    this.channel = options.channel;
  }

  health(): Promise<HealthPayload> {
    return Promise.resolve({
      ok: true,
      service: 'pwa',
      version: '0.1.0-mock',
      time: nowSeconds(),
      details: { mode: 'mock', protocol_version: PROTOCOL_VERSION },
    });
  }

  models(): Promise<ModelsResponse> {
    return Promise.resolve({ models: [...MODEL_OPTIONS], default: DEFAULT_MODEL });
  }

  approvals(): Promise<ApprovalView[]> {
    if (!this.seededApprovals) {
      return this.daemon.seedApprovals().then((seeded) => {
        this.seededApprovals = seeded;
        return seeded.map((approval) => ({ ...approval }));
      });
    }
    return Promise.resolve(this.seededApprovals.map((approval) => ({ ...approval })));
  }

  async decideApproval(
    approval: ApprovalView,
    decision: ApprovalDecision,
    options: DecideApprovalOptions,
  ): Promise<DecideApprovalOutcome> {
    const validation = validateDecision(approval, {
      decision,
      args_hash: approval.args_hash,
      confirm_high_risk: options.confirmHighRisk,
    });
    if (!validation.ok) {
      return { ok: false, code: validation.code, error: validation.message };
    }
    const nonce = options.nonce ?? this.daemon.nonce();
    try {
      const signature = await signApproval(this.identity.signing_private_key, {
        approval_id: approval.approval_id,
        decision,
        args_hash: approval.args_hash,
        nonce,
      });
      const result = await this.daemon.decide(approval, {
        decision,
        args_hash: approval.args_hash,
        signature,
        nonce,
        confirm_high_risk: options.confirmHighRisk,
      });
      if (!result.ok) {
        return { ok: false, code: 'CONFLICT', error: result.error ?? '审批处理失败' };
      }
      return { ok: true, approval: result.updated };
    } catch (error) {
      return {
        ok: false,
        code: 'AUTH_INVALID',
        error: `审批签名失败：${error instanceof Error ? error.message : '未知错误'}`,
      };
    }
  }

  sessions(): Promise<SessionView[]> {
    return Promise.resolve(this.daemon.sessions());
  }

  sessionTail(source: SessionSource, sessionId: string): Promise<ClientTailResult> {
    return Promise.resolve({ events: this.daemon.sessionTail(source, sessionId) });
  }

  qqStatus(): Promise<QqStatus> {
    return Promise.resolve(this.cloneQqStatus());
  }

  async qqSetPermissions(patch: QqPermissionsPatch): Promise<QqStatus> {
    const ownerAfter = patch.owner_id ?? this.qq.owner_id;
    if (patch.mode !== undefined) {
      // Fail-closed parity with the backend: `auto` is stored but not active and
      // cannot be newly selected from the card.
      if (patch.mode.mode === 'auto') {
      }
      if (patch.mode.mode !== 'chat') {
        const ownerPrivate = patch.mode.kind === 'private' && ownerAfter === patch.mode.id;
        if (!ownerPrivate) {
          throw new LuminisClientError(
            'INVALID_REQUEST',
            '群聊永远聊天档；只有 owner 私聊可切换审批/自动',
          );
        }
      }
    }
    this.qq = qqApplyPermissionsPatch(this.qq, patch, nowSeconds());
    return this.cloneQqStatus();
  }

  qqDisconnect(): Promise<{ ok: boolean }> {
    this.qq = { ...this.qq, connected: false };
    return Promise.resolve({ ok: true });
  }

  private cloneQqStatus(): QqStatus {
    return {
      ...this.qq,
      chats: this.qq.chats.map((chat) => ({ ...chat })),
    };
  }

  private memoryList(): MemoryEntry[] {
    if (this.seededMemories === null) this.seededMemories = demoMemories(nowSeconds());
    return this.seededMemories;
  }

  private cloneMemories(): MemoryEntry[] {
    return this.memoryList().map((entry) => ({ ...entry }));
  }

  memories(): Promise<MemoryEntry[]> {
    return Promise.resolve(this.cloneMemories());
  }

  deleteMemory(id: string): Promise<MemoryEntry[]> {
    const list = this.memoryList();
    const next = list.filter((entry) => entry.id !== id);
    if (next.length === list.length) {
      return Promise.reject(new LuminisClientError('NOT_FOUND', '记忆不存在或已被删除'));
    }
    this.seededMemories = next;
    return Promise.resolve(this.cloneMemories());
  }

  approveMemory(id: string): Promise<MemoryEntry[]> {
    const list = this.memoryList();
    const entry = list.find((item) => item.id === id);
    if (entry === undefined) {
      return Promise.reject(new LuminisClientError('NOT_FOUND', '记忆不存在或已被删除'));
    }
    if (entry.status !== 'pending') {
      return Promise.reject(
        new LuminisClientError('INVALID_REQUEST', '只有待确认记忆可以确认保存'),
      );
    }
    this.seededMemories = list.map((item) =>
      item.id === id ? { ...item, status: 'active' as const, updated_at: nowSeconds() } : item,
    );
    return Promise.resolve(this.cloneMemories());
  }

  clearMemories(): Promise<{ ok: boolean }> {
    this.seededMemories = [];
    return Promise.resolve({ ok: true });
  }

  // -- demo todo/alarm CRUD (in-memory only) --------------------------------

  private todoList(): Todo[] {
    if (this.seededTodos === null) this.seededTodos = demoTodos(nowSeconds());
    return this.seededTodos;
  }

  private cloneTodos(): Todo[] {
    return this.todoList().map((todo) => ({ ...todo }));
  }

  todos(): Promise<Todo[]> {
    return Promise.resolve(this.cloneTodos());
  }

  async createTodo(input: TodoDraft): Promise<Todo> {
    const draft = normalizeTodoDraft(input);
    if (!draft.ok) throw new LuminisClientError('INVALID_REQUEST', draft.message);
    const now = nowSeconds();
    const todo: Todo = {
      id: randomId('todo_'),
      ...draft.value,
      status: 'open',
      created_at: now,
      updated_at: now,
      completed_at: null,
    };
    this.seededTodos = sortTodos([...this.todoList(), todo]);
    return { ...todo };
  }

  async updateTodo(id: string, patch: TodoPatch): Promise<Todo> {
    const validation = normalizeTodoPatch(patch);
    if (!validation.ok) throw new LuminisClientError('INVALID_REQUEST', validation.message);
    const list = this.todoList();
    const existing = list.find((todo) => todo.id === id);
    if (existing === undefined) {
      throw new LuminisClientError('NOT_FOUND', '待办不存在或已被删除');
    }
    const updated: Todo = {
      ...existing,
      ...validation.value,
      updated_at: nowSeconds(),
    };
    this.seededTodos = upsertTodo(list, updated);
    return { ...updated };
  }

  async deleteTodo(id: string): Promise<Todo[] | null> {
    const list = this.todoList();
    const next = list.filter((todo) => todo.id !== id);
    if (next.length === list.length) {
      throw new LuminisClientError('NOT_FOUND', '待办不存在或已被删除');
    }
    this.seededTodos = next;
    return this.cloneTodos();
  }

  async toggleTodo(id: string): Promise<Todo> {
    const list = this.todoList();
    const existing = list.find((todo) => todo.id === id);
    if (existing === undefined) {
      throw new LuminisClientError('NOT_FOUND', '待办不存在或已被删除');
    }
    const now = nowSeconds();
    const updated: Todo =
      existing.status === 'open'
        ? { ...existing, status: 'done', completed_at: now, updated_at: now }
        : { ...existing, status: 'open', completed_at: null, updated_at: now };
    this.seededTodos = upsertTodo(list, updated);
    return { ...updated };
  }

  private alarmList(): Alarm[] {
    if (this.seededAlarms === null) this.seededAlarms = demoAlarms(nowSeconds());
    return this.seededAlarms;
  }

  private cloneAlarms(): Alarm[] {
    return this.alarmList().map((alarm) => ({ ...alarm, repeat_days: [...alarm.repeat_days] }));
  }

  private nextAlarmValue(alarm: Alarm, now: number): number | null {
    return alarm.enabled ? nextAlarmAt(alarm, now) : null;
  }

  alarms(): Promise<Alarm[]> {
    return Promise.resolve(this.cloneAlarms());
  }

  async createAlarm(input: AlarmDraft): Promise<Alarm> {
    const draft = normalizeAlarmDraft(input);
    if (!draft.ok) throw new LuminisClientError('INVALID_REQUEST', draft.message);
    const now = nowSeconds();
    const alarm: Alarm = {
      id: randomId('alarm_'),
      ...draft.value,
      next_at: null,
      created_at: now,
      updated_at: now,
    };
    alarm.next_at = this.nextAlarmValue(alarm, now);
    this.seededAlarms = sortAlarms([...this.alarmList(), alarm]);
    return { ...alarm, repeat_days: [...alarm.repeat_days] };
  }

  async updateAlarm(id: string, patch: AlarmPatch): Promise<Alarm> {
    const validation = normalizeAlarmPatch(patch);
    if (!validation.ok) throw new LuminisClientError('INVALID_REQUEST', validation.message);
    const list = this.alarmList();
    const existing = list.find((alarm) => alarm.id === id);
    if (existing === undefined) {
      throw new LuminisClientError('NOT_FOUND', '闹钟不存在或已被删除');
    }
    const now = nowSeconds();
    const updated: Alarm = {
      ...existing,
      ...validation.value,
      updated_at: now,
    };
    updated.next_at = this.nextAlarmValue(updated, now);
    this.seededAlarms = upsertAlarm(list, updated);
    return { ...updated, repeat_days: [...updated.repeat_days] };
  }

  async deleteAlarm(id: string): Promise<Alarm[] | null> {
    const list = this.alarmList();
    const next = list.filter((alarm) => alarm.id !== id);
    if (next.length === list.length) {
      throw new LuminisClientError('NOT_FOUND', '闹钟不存在或已被删除');
    }
    this.seededAlarms = next;
    return this.cloneAlarms();
  }

  async alarmFired(id: string): Promise<AlarmFiredResult> {
    const list = this.alarmList();
    const existing = list.find((alarm) => alarm.id === id);
    if (existing === undefined) {
      throw new LuminisClientError('NOT_FOUND', '闹钟不存在或已被删除');
    }
    const now = nowSeconds();
    const next = existing.repeat_days.length > 0 ? nextAlarmAt(existing, now + 1) : null;
    const enabled = existing.enabled && next !== null;
    const updated: Alarm = {
      ...existing,
      enabled,
      next_at: enabled ? next : null,
      updated_at: now,
    };
    this.seededAlarms = upsertAlarm(list, updated);
    return {
      ok: true,
      alarm: { ...updated, repeat_days: [...updated.repeat_days] },
    };
  }

  streamChat(
    request: LuminisChatRequest,
    handlers: DaemonChatHandlers,
    options: { messageId?: string } = {},
  ): ChatStreamHandle {
    const model = request.model ?? DEFAULT_MODEL;
    const mockRequest: MockStreamRequest = {
      messageId: options.messageId ?? randomId('msg_'),
      text: request.text,
      history: request.history ?? [],
      model,
      thinking: model.thinking ?? 'enabled',
      effort: model.effort ?? 'max',
    };
    let finished = false;
    let aborted = false;
    const daemonHandle = this.daemon.streamChat(mockRequest, {
      onDelta: handlers.onDelta,
      onDone: (done) => {
        finished = true;
        handlers.onDone(done);
      },
      onError: (error) => {
        finished = true;
        handlers.onError(error);
      },
      onAbort: () => {
        finished = true;
        handlers.onAbort?.();
      },
    });
    const clientHandle: ChatStreamHandle = {
      abort: () => {
        if (aborted || finished) return;
        aborted = true;
        this.streams.delete(clientHandle);
        daemonHandle.abort();
        // MockDaemon's abort clears its timers (so it will not invoke onAbort);
        // the client surface still reports the stop explicitly, like daemon mode.
        handlers.onAbort?.();
      },
    };
    this.streams.add(clientHandle);
    return clientHandle;
  }

  async sealEnvelope(
    type: MessageType,
    payload: unknown,
    options: { id?: string; ts?: number } = {},
  ): Promise<Envelope> {
    return sealWithChannel(this.channel, this.identity.device_id, type, payload, options);
  }

  openEnvelope<T = unknown>(envelope: Envelope): Promise<T> {
    return openWithChannel<T>(this.channel, envelope);
  }

  dispose(): void {
    for (const handle of this.streams) handle.abort();
    this.streams.clear();
  }
}

// ---------------------------------------------------------------------------
// Daemon client (real HTTP + SSE)
// ---------------------------------------------------------------------------

interface RawResponse {
  status: number;
  ok: boolean;
  text: string;
  json: unknown;
}

export class DaemonLuminisClient implements LuminisClient {
  readonly mode = 'daemon' as const;
  readonly baseUrl: string;
  private readonly token: string;
  private readonly identity: DeviceIdentityStored;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;
  private readonly deviceId: string;
  private readonly channel: E2EChannel | undefined;
  private readonly clock: () => number;
  private readonly activeStreams = new Map<AbortController, () => void>();

  constructor(options: Omit<DaemonClientOptions, 'mode'>) {
    const baseUrl = (options.baseUrl ?? '').trim().replace(/\/+$/u, '');
    if (!/^https?:\/\/[^\s]+$/iu.test(baseUrl)) {
      throw new LuminisClientError('INVALID_REQUEST', 'daemon base URL 无效（需要 http(s)://…）');
    }
    const token = (options.token ?? '').trim();
    if (!token) throw new LuminisClientError('AUTH_REQUIRED', '缺少 daemon 设备令牌');
    if (!options.identity?.signing_private_key) {
      throw new LuminisClientError('AUTH_REQUIRED', '缺少设备审批签名密钥（请重新配对）');
    }
    if (options.timeoutMs !== undefined && !(options.timeoutMs > 0)) {
      throw new LuminisClientError('INVALID_REQUEST', 'timeoutMs 必须为正数');
    }
    this.baseUrl = baseUrl;
    this.token = token;
    this.identity = options.identity;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.deviceId = options.deviceId ?? options.identity.device_id;
    this.channel = options.channel;
    this.clock = options.clock ?? nowSeconds;
  }

  async health(): Promise<HealthPayload> {
    const health = await this.requestJson<HealthPayload>('/health', { method: 'GET' });
    if (!isRecord(health) || health.ok !== true || typeof health.version !== 'string') {
      throw new LuminisClientError('INTERNAL', 'daemon /health 返回了意外数据');
    }
    return health;
  }

  async models(): Promise<ModelsResponse> {
    const response = await this.requestJson<unknown>('/models', { method: 'GET' });
    if (!isRecord(response) || !Array.isArray(response.models)) {
      throw new LuminisClientError('INTERNAL', 'daemon /models 返回了意外数据');
    }
    return response as unknown as ModelsResponse;
  }

  async approvals(): Promise<ApprovalView[]> {
    const response = await this.requestJson<unknown>('/approvals', { method: 'GET' });
    const rawList = Array.isArray(response)
      ? response
      : isRecord(response) && Array.isArray(response.approvals)
        ? response.approvals
        : null;
    if (!rawList) {
      throw new LuminisClientError('INTERNAL', 'daemon /approvals 返回了意外数据');
    }
    const requestedAt = this.clock();
    return rawList
      .map((item) => approvalRequestFromUnknown(item))
      .filter((item): item is ApprovalRequest => item !== null)
      .map((request) => approvalViewFromRequest(request, requestedAt));
  }

  async decideApproval(
    approval: ApprovalView,
    decision: ApprovalDecision,
    options: DecideApprovalOptions,
  ): Promise<DecideApprovalOutcome> {
    const validation = validateDecision(approval, {
      decision,
      args_hash: approval.args_hash,
      confirm_high_risk: options.confirmHighRisk,
    });
    if (!validation.ok) {
      return { ok: false, code: validation.code, error: validation.message };
    }
    const nonce = options.nonce ?? newApprovalNonce();
    let signature: string;
    try {
      signature = await signApproval(this.identity.signing_private_key, {
        approval_id: approval.approval_id,
        decision,
        args_hash: approval.args_hash,
        nonce,
      });
    } catch (error) {
      return {
        ok: false,
        code: 'AUTH_INVALID',
        error: `审批签名失败：${error instanceof Error ? error.message : '未知错误'}`,
      };
    }
    let response: RawResponse;
    try {
      response = await this.requestRaw('/approval', {
        method: 'POST',
        body: JSON.stringify({
          approval_id: approval.approval_id,
          decision,
          signature,
          nonce,
          args_hash: approval.args_hash,
        }),
      });
    } catch (error) {
      const mapped = chatErrorEvent(approval.approval_id, error);
      return { ok: false, code: mapped.code, error: mapped.message };
    }
    const body = isRecord(response.json) ? response.json : {};
    const updated = approvalViewFromRow(body.approval);
    if (response.ok && body.ok === true) {
      return { ok: true, ...(updated ? { approval: updated } : {}) };
    }
    const parsed = parseErrorBody(body, response.status);
    // Server semantics: a deny is returned as `ok:false` + APPROVAL_DENIED.
    // For the UI it is a successfully *recorded* decision, not a failure.
    if (
      decision === 'deny' &&
      (updated?.status === 'denied' || parsed.code === 'APPROVAL_DENIED')
    ) {
      return { ok: true, ...(updated ? { approval: updated } : {}) };
    }
    return {
      ok: false,
      code: parsed.code,
      error: parsed.message,
      ...(updated ? { approval: updated } : {}),
    };
  }

  async sessions(query: ClientSessionQuery = {}): Promise<SessionView[]> {
    const params = new URLSearchParams();
    params.set('days', String(query.days ?? DEFAULT_SESSION_DAYS));
    if (query.activeOnly !== undefined) params.set('active_only', String(query.activeOnly));
    if (query.source) params.set('source', query.source);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    const response = await this.requestJson<unknown>(`/sessions?${params.toString()}`, {
      method: 'GET',
    });
    const rawList = Array.isArray(response)
      ? response
      : isRecord(response) && Array.isArray(response.sessions)
        ? response.sessions
        : null;
    if (!rawList) {
      throw new LuminisClientError('INTERNAL', 'daemon /sessions 返回了意外数据');
    }
    const fetchedAt = this.clock();
    return rawList
      .map((item) =>
        isRecord(item)
          ? sessionViewFromSummary(item as unknown as SessionSummary, fetchedAt)
          : null,
      )
      .filter((item): item is SessionView => item !== null);
  }

  async sessionTail(
    source: SessionSource,
    sessionId: string,
    query: ClientTailQuery = {},
  ): Promise<ClientTailResult> {
    const params = new URLSearchParams();
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.cursor) params.set('cursor', query.cursor);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const path = `/sessions/${encodeURIComponent(source)}/${encodeURIComponent(sessionId)}/tail${suffix}`;
    const response = await this.requestJson<unknown>(path, { method: 'GET' });
    if (!isRecord(response) || !Array.isArray(response.events)) {
      throw new LuminisClientError('INTERNAL', 'daemon session tail 返回了意外数据');
    }
    const events = (response.events as SessionEvent[])
      .map((event) => sessionEventFromWire(event))
      .filter((event): event is SessionEventView => event !== null);
    return {
      events,
      ...(typeof response.next_cursor === 'string' ? { next_cursor: response.next_cursor } : {}),
    };
  }

  async qqStatus(): Promise<QqStatus> {
    const response = await this.requestJson<unknown>('/qq/status', { method: 'GET' });
    const status = qqStatusFromUnknown(response);
    if (status === null) {
      throw new LuminisClientError('INTERNAL', 'daemon /qq/status 返回了意外数据');
    }
    return status;
  }

  async qqSetPermissions(patch: QqPermissionsPatch): Promise<QqStatus> {
    const response = await this.requestJson<unknown>('/qq/permissions', {
      method: 'POST',
      body: JSON.stringify(qqPermissionsBody(patch)),
    });
    const status = qqStatusFromUnknown(response);
    if (status === null) {
      throw new LuminisClientError('INTERNAL', 'daemon /qq/permissions 返回了意外数据');
    }
    return status;
  }

  async qqDisconnect(): Promise<{ ok: boolean }> {
    const response = await this.requestJson<unknown>('/qq/disconnect', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    if (!isRecord(response) || response.ok !== true) {
      throw new LuminisClientError('INTERNAL', 'daemon /qq/disconnect 未确认断开');
    }
    return { ok: true };
  }

  async memories(): Promise<MemoryEntry[]> {
    const response = await this.requestJson<unknown>('/memories', { method: 'GET' });
    const entries = memoryEntriesFromUnknown(response);
    if (entries === null) {
      throw new LuminisClientError('INTERNAL', 'daemon /memories 返回了意外数据');
    }
    return entries;
  }

  async deleteMemory(id: string): Promise<MemoryEntry[]> {
    const response = await this.requestJson<unknown>(`/memories/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    const entries = memoryEntriesFromUnknown(response);
    if (entries === null) {
      throw new LuminisClientError('INTERNAL', 'daemon 删除记忆后返回了意外数据');
    }
    return entries;
  }

  async clearMemories(): Promise<{ ok: boolean }> {
    const response = await this.requestJson<unknown>('/memories', { method: 'DELETE' });
    if (!isRecord(response) || response.ok !== true) {
      throw new LuminisClientError('INTERNAL', 'daemon /memories 未确认清空');
    }
    return { ok: true };
  }

  async approveMemory(id: string): Promise<MemoryEntry[]> {
    const response = await this.requestJson<unknown>(
      `/memories/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body: JSON.stringify({}) },
    );
    const entries = memoryEntriesFromUnknown(response);
    if (entries === null) {
      throw new LuminisClientError('INTERNAL', 'daemon 确认记忆后返回了意外数据');
    }
    return entries;
  }

  // -- todos ---------------------------------------------------------------

  async todos(): Promise<Todo[]> {
    const response = await this.requestJson<unknown>('/todos', { method: 'GET' });
    const todos = todosFromUnknown(response);
    if (todos === null) {
      throw new LuminisClientError('INTERNAL', 'daemon /todos 返回了意外数据');
    }
    return todos;
  }

  async createTodo(input: TodoDraft): Promise<Todo> {
    const response = await this.requestJson<unknown>('/todos', {
      method: 'POST',
      body: JSON.stringify(todoCreateBody(input)),
    });
    const todo = todoMutationFromResponse(response);
    if (todo === null) {
      throw new LuminisClientError('INTERNAL', 'daemon 创建待办后返回了意外数据');
    }
    return todo;
  }

  async updateTodo(id: string, patch: TodoPatch): Promise<Todo> {
    const response = await this.requestJson<unknown>(`/todos/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(todoPatchBody(patch)),
    });
    const todo = todoMutationFromResponse(response, id);
    if (todo === null) {
      throw new LuminisClientError('INTERNAL', 'daemon 更新待办后返回了意外数据');
    }
    return todo;
  }

  async deleteTodo(id: string): Promise<Todo[] | null> {
    const response = await this.requestJson<unknown>(`/todos/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    assertDeleteAcknowledged(response, 'DELETE /todos');
    // The daemon returns the authoritative remaining list; older/simpler
    // responses without it are accepted as a bare acknowledgement.
    return todosFromUnknown(response);
  }

  async toggleTodo(id: string): Promise<Todo> {
    const response = await this.requestJson<unknown>(`/todos/${encodeURIComponent(id)}/toggle`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    const todo = todoMutationFromResponse(response, id);
    if (todo === null) {
      throw new LuminisClientError('INTERNAL', 'daemon 切换待办状态后返回了意外数据');
    }
    return todo;
  }

  // -- alarms --------------------------------------------------------------

  async alarms(): Promise<Alarm[]> {
    const response = await this.requestJson<unknown>('/alarms', { method: 'GET' });
    const alarms = alarmsFromUnknown(response);
    if (alarms === null) {
      throw new LuminisClientError('INTERNAL', 'daemon /alarms 返回了意外数据');
    }
    return alarms;
  }

  async createAlarm(input: AlarmDraft): Promise<Alarm> {
    const response = await this.requestJson<unknown>('/alarms', {
      method: 'POST',
      body: JSON.stringify(alarmCreateBody(input)),
    });
    const alarm = alarmMutationFromResponse(response);
    if (alarm === null) {
      throw new LuminisClientError('INTERNAL', 'daemon 创建闹钟后返回了意外数据');
    }
    return alarm;
  }

  async updateAlarm(id: string, patch: AlarmPatch): Promise<Alarm> {
    const response = await this.requestJson<unknown>(`/alarms/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(alarmPatchBody(patch)),
    });
    const alarm = alarmMutationFromResponse(response, id);
    if (alarm === null) {
      throw new LuminisClientError('INTERNAL', 'daemon 更新闹钟后返回了意外数据');
    }
    return alarm;
  }

  async deleteAlarm(id: string): Promise<Alarm[] | null> {
    const response = await this.requestJson<unknown>(`/alarms/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    assertDeleteAcknowledged(response, 'DELETE /alarms');
    return alarmsFromUnknown(response);
  }

  async alarmFired(id: string): Promise<AlarmFiredResult> {
    const response = await this.requestJson<unknown>(`/alarms/${encodeURIComponent(id)}/fired`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    const alarm = alarmMutationFromResponse(response, id);
    if (alarm !== null) return { ok: true, alarm };
    // A malformed row must not be mistaken for a bare acknowledgement.
    if (isRecord(response) && response.alarm !== undefined && response.alarm !== null) {
      throw new LuminisClientError('INTERNAL', 'daemon /alarms/:id/fired 返回了意外数据');
    }
    // Some daemon builds only acknowledge; the caller refreshes instead of
    // guessing the advanced `next_at`.
    if (isRecord(response) && response.ok === true) return { ok: true, alarm: null };
    throw new LuminisClientError('INTERNAL', 'daemon /alarms/:id/fired 返回了意外数据');
  }

  streamChat(
    request: LuminisChatRequest,
    handlers: DaemonChatHandlers,
    options: { messageId?: string } = {},
  ): ChatStreamHandle {
    const controller = new AbortController();
    const messageId = options.messageId ?? randomId('msg_');
    const model = request.model ?? DEFAULT_MODEL;
    const parser = new DaemonChatStreamParser({ messageId, model });
    let activeReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    // Cancelling the reader wakes a pending `read()`; aborting the controller
    // alone cannot interrupt a response body that already arrived.
    const stopStream = (): void => {
      try {
        controller.abort();
      } catch {
        /* already aborted */
      }
      try {
        void activeReader?.cancel().catch(() => undefined);
      } catch {
        /* reader already closed */
      }
    };
    this.activeStreams.set(controller, stopStream);

    const finish = (): void => {
      this.activeStreams.delete(controller);
    };

    const dispatch = (events: DaemonChatStreamEvent[]): void => {
      for (const event of events) {
        switch (event.type) {
          case 'delta':
            handlers.onDelta(event.delta);
            break;
          case 'done':
            handlers.onDone(event.done);
            break;
          case 'error':
            handlers.onError(event.error);
            break;
          case 'usage':
            handlers.onUsage?.(event.usage);
            break;
          case 'tool_call':
            handlers.onToolCall?.(event.call);
            break;
          case 'tool_result':
            handlers.onToolResult?.(event.result);
            break;
          case 'approval_required':
            handlers.onApprovalRequired?.(event.approval);
            break;
        }
      }
    };

    const run = async (): Promise<void> => {
      let response: Response;
      let cleanupHeaders: (() => void) | null = null;
      let body: string;
      try {
        body = JSON.stringify({
          text: request.text,
          ...(request.conversation_id ? { conversation_id: request.conversation_id } : {}),
          ...(request.persona_id ? { persona_id: request.persona_id } : {}),
          ...(request.source ? { source: request.source } : {}),
          ...(request.model ? { model: request.model } : {}),
        });
        const fetched = await this.fetchStreamHeaders(
          '/chat',
          { method: 'POST', body },
          controller.signal,
        );
        response = fetched.response;
        cleanupHeaders = fetched.cleanup;
      } catch (error) {
        finish();
        if (controller.signal.aborted) {
          handlers.onAbort?.();
          return;
        }
        handlers.onError(chatErrorEvent(messageId, error));
        return;
      }
      if (!response.ok) {
        cleanupHeaders?.();
        finish();
        const errorBody = await readJsonSafely(response);
        const parsed = parseErrorBody(errorBody, response.status);
        handlers.onError({
          message_id: messageId,
          code: parsed.code,
          message: parsed.message,
          retryable: parsed.retryable,
        });
        return;
      }
      const reader = response.body?.getReader();
      if (!reader) {
        cleanupHeaders?.();
        finish();
        handlers.onError({
          message_id: messageId,
          code: 'INTERNAL',
          message: 'daemon 返回了空响应',
          retryable: true,
        });
        return;
      }
      activeReader = reader;
      const decoder = new TextDecoder();
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          dispatch(parser.push(decoder.decode(value, { stream: true })));
          if (parser.isDone) break;
        }
        if (controller.signal.aborted) {
          handlers.onAbort?.();
          return;
        }
        dispatch(parser.finish());
      } catch (error) {
        if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) {
          handlers.onAbort?.();
          return;
        }
        handlers.onError({
          message_id: messageId,
          code: 'INTERNAL',
          message: 'daemon 流式响应中断',
          retryable: true,
        });
      } finally {
        activeReader = null;
        cleanupHeaders?.();
        finish();
        try {
          await reader.cancel();
        } catch {
          /* reader already closed */
        }
      }
    };

    void run();
    return { abort: stopStream };
  }

  async sealEnvelope(
    type: MessageType,
    payload: unknown,
    options: { id?: string; ts?: number } = {},
  ): Promise<Envelope> {
    return sealWithChannel(this.channel, this.deviceId, type, payload, options);
  }

  openEnvelope<T = unknown>(envelope: Envelope): Promise<T> {
    return openWithChannel<T>(this.channel, envelope);
  }

  dispose(): void {
    for (const stop of this.activeStreams.values()) stop();
    this.activeStreams.clear();
  }

  // -- low-level HTTP -------------------------------------------------------

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    return {
      Authorization: `Bearer ${this.token}`,
      Accept: 'application/json',
      ...extra,
    };
  }

  /**
   * Fetch the response *headers* for an SSE stream. The returned `cleanup()`
   * keeps the external abort listener alive until the stream finishes, so
   * `abort()` also cancels the underlying fetch body.
   */
  private async fetchStreamHeaders(
    path: string,
    init: RequestInit,
    external: AbortSignal,
  ): Promise<{ response: Response; cleanup: () => void }> {
    const controller = new AbortController();
    let timedOut = false;
    const onAbort = (): void => controller.abort();
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', onAbort, { once: true });
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.timeoutMs);
    const cleanup = (): void => {
      clearTimeout(timer);
      external.removeEventListener('abort', onAbort);
    };
    try {
      const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        signal: controller.signal,
        headers: this.headers({
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...((init.headers as Record<string, string> | undefined) ?? {}),
        }),
      });
      return { response, cleanup };
    } catch (error) {
      cleanup();
      if (timedOut) {
        throw new LuminisClientError('EXEC_TIMEOUT', `daemon 请求超时（${this.timeoutMs}ms）`, {
          retryable: true,
        });
      }
      if (external.aborted) {
        throw new LuminisClientError('INTERNAL', '请求已取消');
      }
      throw new LuminisClientError('INTERNAL', '无法连接 daemon（网络错误）', {
        retryable: true,
      });
    }
  }

  private async fetchWithTimeout(
    path: string,
    init: RequestInit,
    withJsonContentType = true,
  ): Promise<Response> {
    const controller = new AbortController();
    const external = init.signal;
    let timedOut = false;
    const onExternalAbort = (): void => controller.abort();
    if (external) {
      if (external.aborted) controller.abort();
      else external.addEventListener('abort', onExternalAbort, { once: true });
    }
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.timeoutMs);
    try {
      return await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        signal: controller.signal,
        headers: this.headers({
          ...(withJsonContentType ? { 'Content-Type': 'application/json' } : {}),
          ...((init.headers as Record<string, string> | undefined) ?? {}),
          ...(withJsonContentType ? {} : { Accept: 'text/event-stream' }),
        }),
      });
    } catch (error) {
      if (timedOut) {
        throw new LuminisClientError('EXEC_TIMEOUT', `daemon 请求超时（${this.timeoutMs}ms）`, {
          retryable: true,
        });
      }
      if (external?.aborted) {
        throw new LuminisClientError('INTERNAL', '请求已取消', { retryable: false });
      }
      throw new LuminisClientError('INTERNAL', '无法连接 daemon（网络错误）', {
        retryable: true,
      });
    } finally {
      clearTimeout(timer);
      external?.removeEventListener('abort', onExternalAbort);
    }
  }

  private async requestRaw(path: string, init: RequestInit): Promise<RawResponse> {
    const response = await this.fetchWithTimeout(path, init);
    const text = await response.text();
    let json: unknown = null;
    if (text.trim().length > 0) {
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
    }
    return { status: response.status, ok: response.ok, text, json };
  }

  private async requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.requestRaw(path, init);
    if (!response.ok) {
      const parsed = parseErrorBody(response.json, response.status);
      throw new LuminisClientError(parsed.code, parsed.message, {
        status: response.status,
        retryable: parsed.retryable,
      });
    }
    if (response.text.trim().length === 0 && response.status !== 204) {
      throw new LuminisClientError('INTERNAL', 'daemon 返回了空响应', { retryable: true });
    }
    if (response.text.trim().length > 0 && response.json === null) {
      throw new LuminisClientError('INTERNAL', 'daemon 返回了无法解析的 JSON');
    }
    return response.json as T;
  }
}

// ---------------------------------------------------------------------------
// E2E envelope helpers shared by both clients
// ---------------------------------------------------------------------------

async function sealWithChannel(
  channel: E2EChannel | undefined,
  defaultDeviceId: string,
  type: MessageType,
  payload: unknown,
  options: { id?: string; ts?: number },
): Promise<Envelope> {
  if (!channel) {
    throw new LuminisClientError('AUTH_REQUIRED', 'E2E 通道密钥未配置（需配对后派生共享密钥）');
  }
  return encryptEnvelope({
    type,
    payload,
    key: channel.key,
    deviceId: channel.deviceId ?? defaultDeviceId,
    ...(options.id ? { id: options.id } : {}),
    ...(options.ts !== undefined ? { ts: options.ts } : {}),
  });
}

async function openWithChannel<T>(channel: E2EChannel | undefined, envelope: Envelope): Promise<T> {
  if (!channel) {
    throw new LuminisClientError('AUTH_REQUIRED', 'E2E 通道密钥未配置（需配对后派生共享密钥）');
  }
  try {
    return await decryptEnvelope<T>(channel.key, envelope);
  } catch (error) {
    throw new LuminisClientError(
      'AUTH_INVALID',
      `E2E 信封解密失败：${error instanceof Error ? error.message : '未知错误'}`,
      { retryable: false },
    );
  }
}

/** Parse JSON from a Response without throwing (used for SSE error bodies). */
async function readJsonSafely(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (!text.trim()) return null;
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}
